import AnyAction from 'store/anyActions';
import ContactHistoryTypes from './contactHistory.types';
import IContactHistoryState from './contactHistory.state';
import { sortContactHistoryInteractions } from './contactHistory.utils';

const defaultState: IContactHistoryState[] = [];

export default function (state: IContactHistoryState[] = defaultState, action: AnyAction): IContactHistoryState[] {
    switch (action.type) {
        case ContactHistoryTypes.ADD_CONTACT_HISTORY: {
            const { attributeSearchName, attributeSearchValue } = action.payload;

            const hasContactHistory = state.some(
                item =>
                    item.attributeSearchName === attributeSearchName &&
                    item.attributeSearchValue === attributeSearchValue
            );
            const updatedState = hasContactHistory ? state : [...state, { ...action.payload, loaded: false }];

            return updatedState.map(item => {
                const { attributeSearchName: itemAttributeSearchName, attributeSearchValue: itemAttributeSearchValue } =
                    item;

                return {
                    ...item,
                    isSelected:
                        itemAttributeSearchName === attributeSearchName &&
                        itemAttributeSearchValue === attributeSearchValue,
                };
            });
        }
        case ContactHistoryTypes.REMOVE_CONTACT_HISTORY: {
            const { attributeSearchName, attributeSearchValue } = action.payload;
            return state.filter(
                item =>
                    item.attributeSearchName !== attributeSearchName &&
                    item.attributeSearchValue !== attributeSearchValue
            );
        }
        case ContactHistoryTypes.SET_CH_INTERACTIONS: {
            const { attributeSearchName, attributeSearchValue, interactions } = action.payload;

            return state.map(item =>
                item.attributeSearchName === attributeSearchName && item.attributeSearchValue === attributeSearchValue
                    ? { ...item, interactions, loaded: true }
                    : item
            );
        }
        case ContactHistoryTypes.SELECT_CH_INTERACTION: {
            const { selectedInteraction, attributeSearchName, attributeSearchValue } = action.payload;
            return state.map(item =>
                item.attributeSearchName === attributeSearchName && item.attributeSearchValue === attributeSearchValue
                    ? { ...item, selectedInteraction }
                    : item
            );
        }
        case ContactHistoryTypes.SET_CH_SELECTED_INSTANCE: {
            const { attributeSearchName, attributeSearchValue } = action.payload;

            return state.map(item => {
                const { attributeSearchName: itemAttributeSearchName, attributeSearchValue: itemAttributeSearchValue } =
                    item;

                return {
                    ...item,
                    isSelected:
                        itemAttributeSearchName === attributeSearchName &&
                        itemAttributeSearchValue === attributeSearchValue,
                };
            });
        }
        case ContactHistoryTypes.DESELECT_CONTACT_HISTORY_INSTANCE: {
            const { attributeSearchName, attributeSearchValue } = action.payload;

            return state.map(item => {
                const { attributeSearchName: itemAttributeSearchName, attributeSearchValue: itemAttributeSearchValue } =
                    item;

                if (
                    itemAttributeSearchName === attributeSearchName &&
                    itemAttributeSearchValue === attributeSearchValue
                )
                    return { ...item, isSelected: false };

                return item;
            });
        }
        case ContactHistoryTypes.ADD_CH_INTERACTION_TRANSCRIPT: {
            const { interactionId, transcript, attributeSearchName, attributeSearchValue } = action.payload;
            return state.map(item => {
                const {
                    interactions,
                    selectedInteraction,
                    attributeSearchName: itemCustomAttributeName,
                    attributeSearchValue: itemCustomAttributeValue,
                } = item;

                if (
                    itemCustomAttributeName !== attributeSearchName ||
                    itemCustomAttributeValue !== attributeSearchValue
                )
                    return item;

                const updatedInteractions = interactions.map(int =>
                    int.ID === interactionId ? { ...int, transcript } : int
                );
                const updatedSelected =
                    selectedInteraction?.ID === interactionId
                        ? { ...selectedInteraction, transcript }
                        : selectedInteraction;

                return { ...item, interactions: updatedInteractions, selectedInteraction: updatedSelected };
            });
        }
        case ContactHistoryTypes.ADD_CH_AUTOMATED_RULES_AUDIT: {
            const { interactionId, automatedRulesAudit, attributeSearchName, attributeSearchValue } = action.payload;
            return state.map(item => {
                const {
                    interactions,
                    selectedInteraction,
                    attributeSearchName: itemCustomAttributeName,
                    attributeSearchValue: itemCustomAttributeValue,
                } = item;

                if (
                    itemCustomAttributeName !== attributeSearchName ||
                    itemCustomAttributeValue !== attributeSearchValue
                )
                    return item;

                const updatedInteractions = interactions.map(int =>
                    int.ID === interactionId ? { ...int, automatedRulesAudit } : int
                );
                const updatedSelected =
                    selectedInteraction?.ID === interactionId
                        ? { ...selectedInteraction, automatedRulesAudit }
                        : selectedInteraction;

                return { ...item, interactions: updatedInteractions, selectedInteraction: updatedSelected };
            });
        }
        case ContactHistoryTypes.ADD_CH_QUEUED_TASKS_AUDIT: {
            const { interactionId, queuedTasksAudit, attributeSearchName, attributeSearchValue } = action.payload;
            return state.map(item => {
                const {
                    interactions,
                    selectedInteraction,
                    attributeSearchName: itemCustomAttributeName,
                    attributeSearchValue: itemCustomAttributeValue,
                } = item;

                if (
                    itemCustomAttributeName !== attributeSearchName ||
                    itemCustomAttributeValue !== attributeSearchValue
                )
                    return item;

                const updatedInteractions = interactions.map(int =>
                    int.ID === interactionId ? { ...int, queuedTasksAudit } : int
                );
                const updatedSelected =
                    selectedInteraction?.ID === interactionId
                        ? { ...selectedInteraction, queuedTasksAudit }
                        : selectedInteraction;

                return { ...item, interactions: updatedInteractions, selectedInteraction: updatedSelected };
            });
        }
        case ContactHistoryTypes.SORT_CH_INTERACTIONS: {
            const { sort } = action.payload;
            return state.map(item => {
                if (item.isSelected) {
                    const sortedInteractions = sortContactHistoryInteractions(item.interactions, sort);
                    return { ...item, interactions: [...sortedInteractions], sort };
                }

                return item;
            });
        }
        case ContactHistoryTypes.SET_CH_VIEW_PREVIOUS_EMAIL_MESSAGES: {
            const { messageId, value } = action.payload;

            return state.map(item => {
                const { interactions, selectedInteraction } = item;

                if (item.isSelected) {
                    const updatedInteractions = interactions.map(int =>
                        int.messageId === messageId ? { ...int, viewPreviousEmailMessages: value } : int
                    );
                    const updatedSelected =
                        selectedInteraction?.messageId === messageId
                            ? { ...selectedInteraction, viewPreviousEmailMessages: value }
                            : selectedInteraction;
                    return { ...item, interactions: updatedInteractions, selectedInteraction: updatedSelected };
                }
                return item;
            });
        }
        case ContactHistoryTypes.ADD_CH_TRANSFORMED_ATTACHMENTS: {
            const { messageId, transformedAttachments, attributeSearchName, attributeSearchValue } = action.payload;

            return state.map(item => {
                const {
                    interactions,
                    selectedInteraction,
                    attributeSearchName: itemCustomAttributeName,
                    attributeSearchValue: itemCustomAttributeValue,
                } = item;

                if (
                    itemCustomAttributeName !== attributeSearchName ||
                    itemCustomAttributeValue !== attributeSearchValue
                )
                    return item;

                const updatedInteractions = interactions.map(int =>
                    int.messageId === messageId
                        ? {
                              ...int,
                              emailMessages: int.emailMessages?.map(emailMessage =>
                                  emailMessage.messageId === messageId
                                      ? { ...emailMessage, attachments: transformedAttachments }
                                      : emailMessage
                              ),
                          }
                        : int
                );
                const updatedSelected = selectedInteraction
                    ? {
                          ...selectedInteraction,
                          emailMessages: selectedInteraction.emailMessages?.map(emailMessage =>
                              emailMessage.messageId === messageId
                                  ? { ...emailMessage, attachments: transformedAttachments }
                                  : emailMessage
                          ),
                      }
                    : selectedInteraction;
                return { ...item, interactions: updatedInteractions, selectedInteraction: updatedSelected };
            });
        }
        default:
            return state;
    }
}
